type RecordLookupResult = {
  tableName: string;
  id: string;
  recordName?: string | null;
  tenantId?: string | null;
  tenantName?: string | null;
  facilityId?: string | null;
  facilityName?: string | null;
  reportId?: string | null;
  chemicalId?: string | null;
  productId?: string | null;
  reportingYear?: number | null;
  workflowId?: string | null;
};

function enc(val: string): string {
  return encodeURIComponent(val);
}

/**
 * Given a record lookup result, return the best URL to navigate to.
 * Returns null if no navigable route can be determined.
 */
export function getRouteForRecord(result: RecordLookupResult): string | null {
  const { tableName, id, tenantId, facilityId, reportId, chemicalId, productId } = result;

  // Staff routes (no tenantId required)
  switch (tableName) {
    case "TierIIReport":
      return `/staff/fulfillment/r/${enc(reportId ?? id)}`;

    case "Workflow":
      if (reportId) return `/staff/fulfillment/r/${enc(reportId)}`;
      return `/staff/fulfillment/workflows`;

    case "Activity":
      if (reportId) return `/staff/fulfillment/r/${enc(reportId)}/activities/${enc(id)}`;
      return null;

    case "Cohort":
      return `/staff/cohorts/${enc(id)}`;

    case "Payment":
      return `/staff/payments/${enc(id)}`;

    case "Job":
      return `/staff/jobs/${enc(id)}`;

    case "Communication":
      return `/staff/mail-room/status/${enc(id)}`;

    case "Lepc":
      return `/staff/agencies/lepcs/${enc(id)}`;

    case "FireDepartment":
      return `/staff/agencies/fireDepartments/${enc(id)}`;
  }

  // Customer routes (require tenantId)
  if (!tenantId) return null;
  const t = enc(tenantId);

  switch (tableName) {
    case "Tenant":
      return `/o/${t}`;

    case "Facility":
      return `/o/${t}/facilities/${enc(id)}/profile`;

    case "FacilityChemical":
      if (facilityId) {
        return `/o/${t}/chemicals/inventory/${enc(facilityId)}/chemicals/${enc(id)}`;
      }
      return `/o/${t}`;

    case "ReportingFacilityChemical":
      // Navigate to the facility's chemical inventory page
      if (facilityId) {
        return `/o/${t}/chemicals/inventory/${enc(facilityId)}`;
      }
      return `/o/${t}`;

    case "FacilityChemicalMeasurement":
      if (facilityId && chemicalId) {
        return `/o/${t}/chemicals/inventory/${enc(
          facilityId
        )}/chemicals/measurements/${enc(chemicalId)}`;
      }
      if (facilityId) return `/o/${t}/chemicals/inventory/${enc(facilityId)}`;
      return `/o/${t}`;

    case "FacilityProduct":
      if (facilityId) {
        return `/o/${t}/chemicals/inventory/${enc(facilityId)}/products/${enc(id)}`;
      }
      return `/o/${t}`;

    case "FacilityProductMeasurement":
      if (facilityId && productId) {
        return `/o/${t}/chemicals/inventory/${enc(
          facilityId
        )}/products/measurements/${enc(productId)}`;
      }
      if (facilityId) return `/o/${t}/chemicals/inventory/${enc(facilityId)}`;
      return `/o/${t}`;

    case "Product":
    case "Chemical":
      return `/o/${t}/chemicals/catalog`;

    case "Person":
      return `/o/${t}/contacts`;

    case "Document":
      return `/o/${t}/documents/${enc(id)}`;

    case "Credential":
      return `/o/${t}/credentials/${enc(id)}`;

    case "StorageLocation":
      if (facilityId) return `/o/${t}/chemicals/inventory/${enc(facilityId)}`;
      return `/o/${t}`;

    case "WasteLineUnified":
      if (facilityId) {
        return `/o/${t}/waste/facilities/${enc(facilityId)}/manifest-line-items/${enc(id)}`;
      }
      return `/o/${t}/waste/overview`;

    case "WasteStream":
      if (facilityId) {
        return `/o/${t}/waste/facilities/${enc(facilityId)}/wasteStream/${enc(id)}`;
      }
      return `/o/${t}/waste/overview`;

    case "ProcessedManifest":
      return `/o/${t}/waste/manifests/${enc(id)}`;

    case "Submission":
      return `/o/${t}/waste/submissions/${enc(id)}`;

    default:
      return `/staff/implementation/${t}`;
  }
}

export type NavigationLink = {
  label: string;
  path: string;
};

/**
 * Produce a set of contextual navigation links for any record lookup result.
 * Includes a direct link (if routable) plus links to parent entities.
 */
export function getNavigationLinks(result: RecordLookupResult): NavigationLink[] {
  const links: NavigationLink[] = [];
  const { tenantId, facilityId, reportId } = result;

  // Direct link for this record
  const directRoute = getRouteForRecord(result);
  if (directRoute) {
    const directLabel = result.recordName
      ? `${result.tableName}: ${result.recordName}`
      : `${result.tableName} (direct)`;
    links.push({ label: directLabel, path: directRoute });
  }

  // Tenant link
  if (tenantId) {
    links.push({
      label: result.tenantName ? `Tenant: ${result.tenantName}` : "Tenant",
      path: `/o/${enc(tenantId)}`,
    });
  }

  // Facility link
  if (facilityId && tenantId) {
    links.push({
      label: result.facilityName ? `Facility: ${result.facilityName}` : "Facility",
      path: `/o/${enc(tenantId)}/facilities/${enc(facilityId)}/profile`,
    });
  }

  // Report link (staff fulfillment)
  if (reportId) {
    links.push({
      label: "Report (staff)",
      path: `/staff/fulfillment/r/${enc(reportId)}`,
    });
  }

  // Staff implementation link
  if (tenantId) {
    links.push({
      label: "Implementation",
      path: `/staff/implementation/${enc(tenantId)}`,
    });
  }

  return links;
}
